import {
  autoGenerate,
  cancel,
  checkNumber,
  get,
  index,
  post,
  put,
} from "src/api/credit-note";

import CreditNoteForm from "src/modules/accounts/components/forms/CreditNoteForm.vue";
import CreditNoteRequestForm from "src/modules/purchase-order/components/forms/CreditNoteRequestForm.vue";
import { findIndex } from "src/assets/scripts/utilities/helpers";
import CreditNoteDetails from "src/modules/credit-notes/components/CreditNoteDetails.vue";

const getInitialState = () => {
  return {
    creditNotes: [],
    creditNote: {},
  };
};

export const useCreditNoteStore = defineStore("credit-note", () => {
  const formState = ref({
    edit: false,
    open: false,
    view: false,
  });

  const toggleCreditNoteForm = (state = {}) => {
    formState.value = {
      edit: false,
      open: !formState.value.open,
      preview: false,
      view: false,
      ...state,
    };

    const { toggleRightDrawer, toggleNestedRightDrawer } = useAppStore();

    if (Object.hasOwn(unref(formState), "level")) {
      toggleNestedRightDrawer({
        component: CreditNoteForm,
        props: unref(formState).props,
        level: unref(formState).level,
        open: unref(formState).open,
      });
    } else {
      toggleRightDrawer(unref(formState).open, CreditNoteForm);
    }
  };

  const toggleCreditNoteRequestForm = (state = {}) => {
    formState.value = {
      edit: false,
      open: !formState.value.open,
      view: false,
      ...state,
    };

    const { toggleRightDrawer } = useAppStore();

    toggleRightDrawer(unref(formState).open, CreditNoteRequestForm);
  };

  const {
    state: creditNoteDetailsDrawerState,
    toggle: toggleCreditNoteDetailsDrawer,
  } = useDrawer({
    component: CreditNoteDetails,
  });

  let state = reactive(getInitialState());

  const addCreditNote = (creditNote) => {
    if (state.creditNotes) {
      state.creditNotes.unshift(creditNote);
    } else {
      state.creditNotes = [creditNote];
    }
  };

  const setCreditNote = (updatedCreditNote) => {
    state.creditNotes[findIndex(state.creditNotes, updatedCreditNote, "_id")] =
      updatedCreditNote;

    state.creditNote = updatedCreditNote;
  };

  const autoGenerateCreditNote = async (creditNote) => {
    try {
      const response = await autoGenerate({ _id: creditNote._id });

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const cancelCreditNote = async (params) => {
    try {
      const response = await cancel(params);

      const { getCounts } = useAccountStore();

      await Promise.all([getCounts(), getCreditNotes()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getCreditNote = async ({ id, source = "kam" }) => {
    try {
      state.creditNote = state.creditNotes.find(
        (creditNote) => creditNote._id === id,
      );

      const response = await get(id, source);

      state.creditNote = response.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const filters = ref({
    po_id: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  const getCreditNotes = async (options = {}) => {
    try {
      options = unref(options);

      if (options?.filters) {
        filters.value = options?.filters;

        delete options.filters;
      }

      const response = await index({
        params: unref(params),
        ...options,
      });

      state.creditNotes = response.data?.data?.data;

      pagination.value.rowsNumber = response.data?.data?.count;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const postCreditNote = async (creditNote) => {
    try {
      const response = await post(creditNote);

      const { getCounts } = usePurchaseOrderStore();

      await getCounts(response.data?.data?.po_id);

      addCreditNote(response.data?.data);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putCreditNote = async (creditNote) => {
    try {
      const response = await put(creditNote);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const addFile = () => {
    getCreditNotes({
      filters: { ...filters, stage: "Not Started" },
    });
  };

  const updateFile = (file) => {
    const cnIndex = findIndex(state.creditNotes, { _id: file.cn_id }, "_id");

    const fileIndex = findIndex(
      state.creditNotes[cnIndex].files,
      { _id: file._id },
      "_id",
    );

    if (cnIndex !== -1 && fileIndex !== -1) {
      if (file.status === "Active") {
        state.creditNotes[cnIndex].files[fileIndex] = file;
      } else {
        state.creditNotes[cnIndex].files.splice(fileIndex, 1);
      }
    }
  };

  const isDuplicate = async (number) => {
    const response = await checkNumber(number);

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    creditNoteDetailsDrawerState,
    toggleCreditNoteDetailsDrawer,
    toggleCreditNoteForm,
    toggleCreditNoteRequestForm,

    filter,
    filters,
    pagination,

    ...toRefs(state),

    setCreditNote,

    addFile,
    updateFile,

    getCreditNote,
    getCreditNotes,
    postCreditNote,
    putCreditNote,

    addCreditNote,
    autoGenerateCreditNote,
    cancelCreditNote,
    isDuplicate,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCreditNoteStore, import.meta.hot));
}
